import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

export const CATEGORIES = [['nonprofit', 'Nonprofit'], ['for_profit', 'For-Profit Business'], ['passion', 'Passion / Hobby']];

const inputCls = 'w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm focus:border-[#1D4ED8] outline-none';

export default function VisionDetailsDialog({ open, onClose, onSubmit, initial, isAdmin, folders = [], creating, allowClientPick }) {
  const [person, setPerson] = useState('');
  const [business, setBusiness] = useState('');
  const [category, setCategory] = useState('');
  const [folderId, setFolderId] = useState('');
  const [clientKey, setClientKey] = useState('self');
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPerson(initial?.client_name || ''); setBusiness(initial?.business_name || '');
    setCategory(initial?.category || ''); setFolderId(initial?.folder_id || ''); setClientKey('self');
    if (isAdmin && creating && allowClientPick && !people.length) {
      ax.get(`${BACKEND}/api/admin/clarity-hub/people`).then((r) => setPeople(r.data.people || [])).catch(() => {});
    }
  }, [open, initial, isAdmin, creating, allowClientPick, people.length]);

  const pickClient = (key) => {
    setClientKey(key);
    const p = people.find((x) => x.key === key);
    if (p && key !== 'self' && !person) setPerson(p.name || '');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!person.trim() && !business.trim()) return;
    setBusy(true);
    const body = { person_name: person.trim(), business_name: business.trim(), category };
    if (isAdmin) body.folder_id = folderId || null;
    if (isAdmin && creating && allowClientPick && clientKey !== 'self') body.client_key = clientKey;
    try { await onSubmit(body); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="vision-details-dialog">
        <DialogHeader>
          <DialogTitle>{creating ? 'Start a Website Vision' : 'Edit vision details'}</DialogTitle>
          <DialogDescription>Give it a real name so it's easy to find later.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {isAdmin && creating && allowClientPick && (
            <label className="block text-xs font-semibold text-[#64748B]">Link to a client (optional)
              <select value={clientKey} onChange={(e) => pickClient(e.target.value)} data-testid="vision-details-client" className={`${inputCls} mt-1 bg-white`}>
                <option value="self">No client account / Me (DCA)</option>
                {people.filter((p) => p.key !== 'self').map((p) => <option key={p.key} value={p.key}>{p.name}{p.email ? ` (${p.email})` : ''}</option>)}
              </select>
            </label>
          )}
          <label className="block text-xs font-semibold text-[#64748B]">Person's name
            <input value={person} onChange={(e) => setPerson(e.target.value)} placeholder="e.g. Jasmine Cole" data-testid="vision-details-person" className={`${inputCls} mt-1`} />
          </label>
          <label className="block text-xs font-semibold text-[#64748B]">Business / project name <span className="font-normal text-[#94A3B8]">(leave blank if they don't have one yet)</span>
            <input value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="e.g. Purrfect Paws" data-testid="vision-details-business" className={`${inputCls} mt-1`} />
          </label>
          <div>
            <div className="text-xs font-semibold text-[#64748B] mb-1">Type</div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setCategory(category === k ? '' : k)} data-testid={`vision-details-cat-${k}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${category === k ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8]'}`}>{l}</button>
              ))}
            </div>
          </div>
          {isAdmin && folders.length > 0 && (
            <label className="block text-xs font-semibold text-[#64748B]">Folder
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)} data-testid="vision-details-folder" className={`${inputCls} mt-1 bg-white`}>
                <option value="">{creating ? 'Auto (based on type)' : 'Unfiled'}</option>
                {folders.map((f) => <option key={f.folder_id} value={f.folder_id}>{f.name}</option>)}
              </select>
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-slate-100">Cancel</button>
            <button type="submit" disabled={busy || (!person.trim() && !business.trim())} data-testid="vision-details-save"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#1D4ED8] text-white text-sm font-bold hover:bg-[#1E40AF] disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />} {creating ? 'Create vision' : 'Save'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
